import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/lib/picker-search";
import { BarChart3, Plus } from "lucide-react";
import { useState } from "react";
import type { PerformanceEntry, StrategyNode } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export default function PerformancePage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>({});

  const { data: entries, isLoading } = useQuery<PerformanceEntry[]>({
    queryKey: [`/api/brands/${brandId}/performance`],
    enabled: !!brandId,
  });
  const { data: nodes } = useQuery<StrategyNode[]>({
    queryKey: [`/api/brands/${brandId}/strategy-nodes`],
    enabled: !!brandId,
  });

  const nodeMap = new Map(nodes?.map(n => [n.id, n]) || []);

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, string>) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/performance`, {
        ...data,
        spend: data.spend ? parseFloat(data.spend) : null,
        impressions: data.impressions ? parseInt(data.impressions) : null,
        clicks: data.clicks ? parseInt(data.clicks) : null,
        purchases: data.purchases ? parseInt(data.purchases) : null,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/performance`] });
      setOpen(false);
      setFormData({});
      toast({ title: "Performance entry created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap fade-in-up">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-performance-title">Performance</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Track test results and performance data by strategy node</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-full px-5" data-testid="button-create-performance">
          <Plus className="h-4 w-4 mr-1.5" /> Log Performance
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}</div>
      ) : entries && entries.length > 0 ? (
        <div className="space-y-3">
          {entries.map(entry => {
            const node = entry.strategyNodeId ? nodeMap.get(entry.strategyNodeId) : undefined;
            return (
              <Card key={entry.id} data-testid={`card-performance-${entry.id}`}>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm">{entry.testName || "Unnamed test"}</h3>
                        {entry.platform && <Badge variant="secondary" className="text-xs">{entry.platform}</Badge>}
                        {entry.channel && <Badge variant="secondary" className="text-xs">{entry.channel}</Badge>}
                      </div>
                    </div>
                    {node && <p className="text-xs text-muted-foreground">Node: {node.angleTitle}</p>}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {entry.spend && <div><span className="text-xs text-muted-foreground">Spend</span><p className="text-sm font-medium">${Number(entry.spend).toFixed(2)}</p></div>}
                      {entry.impressions && <div><span className="text-xs text-muted-foreground">Impressions</span><p className="text-sm font-medium">{entry.impressions.toLocaleString()}</p></div>}
                      {entry.clicks && <div><span className="text-xs text-muted-foreground">Clicks</span><p className="text-sm font-medium">{entry.clicks.toLocaleString()}</p></div>}
                      {entry.ctr && <div><span className="text-xs text-muted-foreground">CTR</span><p className="text-sm font-medium">{(Number(entry.ctr) * 100).toFixed(2)}%</p></div>}
                      {entry.purchases && <div><span className="text-xs text-muted-foreground">Purchases</span><p className="text-sm font-medium">{entry.purchases}</p></div>}
                      {entry.roas && <div><span className="text-xs text-muted-foreground">ROAS</span><p className="text-sm font-medium">{Number(entry.roas).toFixed(2)}x</p></div>}
                    </div>
                    {entry.notes && <p className="text-xs text-muted-foreground mt-1">{entry.notes}</p>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BarChart3 className="h-10 w-10 text-muted-foreground mb-3" />
            <h3 className="font-medium mb-1">No performance data yet</h3>
            <p className="text-sm text-muted-foreground">Log your first test results to start building performance memory</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Log Performance</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Strategy Node *</Label>
              <SearchableSelect
                value={formData.strategyNodeId || ""}
                onValueChange={v => setFormData(p => ({ ...p, strategyNodeId: v }))}
                placeholder="Select node..."
                testId="select-performance-node"
                options={(nodes ?? []).map(n => ({ value: n.id, label: n.angleTitle }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Test Name</Label>
              <Input value={formData.testName || ""} onChange={e => setFormData(p => ({ ...p, testName: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Platform</Label><Input value={formData.platform || ""} onChange={e => setFormData(p => ({ ...p, platform: e.target.value }))} placeholder="Meta, TikTok..." /></div>
              <div className="space-y-2"><Label>Channel</Label><Input value={formData.channel || ""} onChange={e => setFormData(p => ({ ...p, channel: e.target.value }))} placeholder="Paid Social..." /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Spend</Label><Input type="number" value={formData.spend || ""} onChange={e => setFormData(p => ({ ...p, spend: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Impressions</Label><Input type="number" value={formData.impressions || ""} onChange={e => setFormData(p => ({ ...p, impressions: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Clicks</Label><Input type="number" value={formData.clicks || ""} onChange={e => setFormData(p => ({ ...p, clicks: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Purchases</Label><Input type="number" value={formData.purchases || ""} onChange={e => setFormData(p => ({ ...p, purchases: e.target.value }))} /></div>
              <div className="space-y-2"><Label>ROAS</Label><Input type="number" step="0.01" value={formData.roas || ""} onChange={e => setFormData(p => ({ ...p, roas: e.target.value }))} /></div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes || ""} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => createMutation.mutate(formData)} disabled={!formData.strategyNodeId || createMutation.isPending}>
              {createMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
